import _asyncToGenerator from "@babel/runtime/helpers/asyncToGenerator";
import _classCallCheck from "@babel/runtime/helpers/classCallCheck";
import _createClass from "@babel/runtime/helpers/createClass";
import _assertThisInitialized from "@babel/runtime/helpers/assertThisInitialized";
import _inherits from "@babel/runtime/helpers/inherits";
import _possibleConstructorReturn from "@babel/runtime/helpers/possibleConstructorReturn";
import _getPrototypeOf from "@babel/runtime/helpers/getPrototypeOf";
import _regeneratorRuntime from "@babel/runtime/regenerator";

function _createSuper(Derived) { var hasNativeReflectConstruct = _isNativeReflectConstruct(); return function _createSuperInternal() { var Super = _getPrototypeOf(Derived), result; if (hasNativeReflectConstruct) { var NewTarget = _getPrototypeOf(this).constructor; result = Reflect.construct(Super, arguments, NewTarget); } else { result = Super.apply(this, arguments); } return _possibleConstructorReturn(this, result); }; }

function _isNativeReflectConstruct() { if (typeof Reflect === "undefined" || !Reflect.construct) return false; if (Reflect.construct.sham) return false; if (typeof Proxy === "function") return true; try { Boolean.prototype.valueOf.call(Reflect.construct(Boolean, [], function () {})); return true; } catch (e) { return false; } }

function _classPrivateMethodGet(receiver, privateSet, fn) { if (!privateSet.has(receiver)) { throw new TypeError("attempted to get private field on non-instance"); } return fn; }

/**
 * @file Defines `InstrumentBase` to be used as the super of the Instrument classes implemented by each feature.
 * Inherits and executes the `checkConfiguration` method from [FeatureBase]{@link ./feature-base}, which also
 * exposes the `blocked` property.
 */
import { drain, registerDrain } from '../../common/drain/drain';
import { FeatureBase } from './feature-base';
import { onWindowLoad } from '../../common/window/load';
import { isBrowserScope } from '../../common/constants/runtime';
import { warn } from '../../common/util/console';
import { FEATURE_NAMES } from '../../loaders/features/features';
import { hasReplayPrerequisite } from '../session_replay/shared/utils';
import { canEnableSessionTracking } from './feature-gates';
import { single } from '../../common/util/invoke';
/**
 * Base class for instrumenting a feature.
 * @extends FeatureBase
 */

var _shouldImportAgg = /*#__PURE__*/new WeakSet();

export var InstrumentBase = /*#__PURE__*/function (_FeatureBase) {
  _inherits(InstrumentBase, _FeatureBase);

  var _super = _createSuper(InstrumentBase);

  /**
   * Instantiate InstrumentBase.
   * @param {string} agentIdentifier - The unique ID of the instantiated agent (relative to global scope).
   * @param {string} featureName - The name of the feature module (used to construct file path).
   * @param {boolean} [auto=true] - Determines whether the feature should automatically register to have the draining
   *     of its pooled instrumentation data handled by the agent's centralized drain functionality, rather than draining
   *     immediately. Primarily useful for fine-grained control in tests.
   */
  function InstrumentBase(agentRef, _featureName) {
    var _this;

    var auto = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : true;

    _classCallCheck(this, InstrumentBase);

    _this = _super.call(this, agentRef.agentIdentifier, _featureName);

    _shouldImportAgg.add(_assertThisInitialized(_this));

    _this.auto = auto;
    /** @type {Function | undefined} This should be set by any derived Instrument class if it has things to do when feature fails or is killed. */

    _this.abortHandler = undefined;
    /**
     * @type {import('./aggregate-base').AggregateBase} Holds the reference to the feature's aggregate module counterpart, if and after it has been initialized. This may not be assigned until after page loads!
     * The only purpose of this for now is to expose it to the NREUM interface, as the feature's instrument instance is already exposed.
    */

    _this.featAggregate = undefined;
    /**
     * @type {Promise} Assigned immediately after @see importAggregator runs. Serves as a signal for when the inner async fn finishes execution. Useful for features to await
     * one another if there are inter-features dependencies.
     * TODO: This is only used for the SPA feature component tests and should be refactored out.
    */

    _this.onAggregateImported = undefined;
    /** used in conjunction with newrelic.start() to defer harvesting in features */

    if (agentRef.init[_this.featureName].autoStart === false) _this.auto = false;
    /** if the feature requires opt-in (!auto-start), it will get registered once the api has been called */

    if (_this.auto) registerDrain(agentRef.agentIdentifier, _featureName);else {
      _this.ee.on('manual-start-all', single(function () {
        // register the feature to drain only once the API has been called, it will drain when importAggregator finishes for all the features
        // called by the api in that cycle
        registerDrain(agentRef.agentIdentifier, _this.featureName);
        _this.auto = true;

        _this.importAggregator(agentRef);
      }));
    }
    return _this;
  }
  /**
   * Lazy-load the latter part of the feature: its aggregator. This method is called by the first part of the feature
   * (the instrumentation) when instrumentation is complete.
   * @param agentRef - reference to the base agent ancestor that this feature belongs to
   * @param {Object} [argsObjFromInstrument] - any values or references to pass down to aggregate
   * @returns void
   */


  _createClass(InstrumentBase, [{
    key: "importAggregator",
    value: function importAggregator(agentRef) {
      var _this2 = this;

      var argsObjFromInstrument = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
      if (this.featAggregate || !this.auto) return;
      var loadedSuccessfully;
      this.onAggregateImported = new Promise(function (resolve) {
        loadedSuccessfully = resolve;
      });

      var importLater = /*#__PURE__*/function () {
        var _ref = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee() {
          var session, _yield$import, setupAgentSession, _this2$abortHandler, _yield$agentRef$share, EventAggregator, _yield$import2, lazyFeatureLoader, _yield$lazyFeatureLoa, Aggregate, _this2$abortHandler2;

          return _regeneratorRuntime.wrap(function _callee$(_context) {
            while (1) {
              switch (_context.prev = _context.next) {
                case 0:
                  _context.prev = 0;

                  if (!canEnableSessionTracking(_this2.agentIdentifier)) {
                    _context.next = 7;
                    break;
                  }

                  _context.next = 4;
                  return import(
                  /* webpackChunkName: "session-manager" */
                  './agent-session');

                case 4:
                  _yield$import = _context.sent;
                  setupAgentSession = _yield$import.setupAgentSession;
                  session = setupAgentSession(agentRef);

                case 7:
                  _context.next = 14;
                  break;

                case 9:
                  _context.prev = 9;
                  _context.t0 = _context["catch"](0);
                  warn(20, _context.t0);

                  _this2.ee.emit('internal-error', [_context.t0]);

                  if (_this2.featureName === FEATURE_NAMES.sessionReplay) (_this2$abortHandler = _this2.abortHandler) === null || _this2$abortHandler === void 0 ? void 0 : _this2$abortHandler.call(_this2); // SR should stop recording if session DNE

                case 14:
                  _context.prev = 14;

                  if (agentRef.sharedAggregator) {
                    _context.next = 24;
                    break;
                  }

                  agentRef.sharedAggregator = import(
                  /* webpackChunkName: "shared-aggregator" */
                  '../../common/aggregate/event-aggregator');
                  _context.next = 19;
                  return agentRef.sharedAggregator;

                case 19:
                  _yield$agentRef$share = _context.sent;
                  EventAggregator = _yield$agentRef$share.EventAggregator;
                  agentRef.sharedAggregator = new EventAggregator();
                  _context.next = 26;
                  break;

                case 24:
                  _context.next = 26;
                  return agentRef.sharedAggregator;

                case 26:
                  if (_classPrivateMethodGet(_this2, _shouldImportAgg, _shouldImportAgg2).call(_this2, _this2.featureName, session)) {
                    _context.next = 30;
                    break;
                  }

                  drain(_this2.agentIdentifier, _this2.featureName);
                  loadedSuccessfully(false); // aggregate module isn't loaded at all

                  return _context.abrupt("return");

                case 30:
                  _context.next = 32;
                  return import(
                  /* webpackChunkName: "lazy-feature-loader" */
                  './lazy-feature-loader');

                case 32:
                  _yield$import2 = _context.sent;
                  lazyFeatureLoader = _yield$import2.lazyFeatureLoader;
                  _context.next = 36;
                  return lazyFeatureLoader(_this2.featureName, 'aggregate');

                case 36:
                  _yield$lazyFeatureLoa = _context.sent;
                  Aggregate = _yield$lazyFeatureLoa.Aggregate;
                  _this2.featAggregate = new Aggregate(agentRef, argsObjFromInstrument);
                  loadedSuccessfully(true);
                  _context.next = 49;
                  break;

                case 42:
                  _context.prev = 42;
                  _context.t1 = _context["catch"](14);
                  warn(34, _context.t1);
                  (_this2$abortHandler2 = _this2.abortHandler) === null || _this2$abortHandler2 === void 0 ? void 0 : _this2$abortHandler2.call(_this2); // undo any important alterations made to the page
                  // not supported yet but nice to do: "abort" this agent's EE for this feature specifically

                  drain(_this2.agentIdentifier, _this2.featureName, true);
                  loadedSuccessfully(false);
                  if (_this2.ee) _this2.ee.abort();

                case 49:
                case "end":
                  return _context.stop();
              }
            }
          }, _callee, null, [[0, 9], [14, 42]]);
        }));

        return function importLater() {
          return _ref.apply(this, arguments);
        };
      }(); // For regular web pages, we want to wait and lazy-load the aggregator only after all page resources are loaded.
      // Non-browser scopes (i.e. workers) have no `window.load` event, so the aggregator can be lazy-loaded immediately.


      if (!isBrowserScope) importLater();else onWindowLoad(function () {
        return importLater();
      }, true);
    }
    /**
    * Make a determination if an aggregate class should even be imported
    * @param {string} featureName
    * @param {import('../../common/session/session-entity').SessionEntity} session
    * @returns
    */

  }]);

  return InstrumentBase;
}(FeatureBase);

function _shouldImportAgg2(featureName, session) {
  switch (featureName) {
    case FEATURE_NAMES.sessionReplay:
      // the session manager must be initialized successfully for Replay & Trace features
      return hasReplayPrerequisite(this.agentIdentifier) && !!session;

    case FEATURE_NAMES.sessionTrace:
      return !!session;

    default:
      return true;
  }
}